import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/primitives'
import { t } from '@/i18n'

/**
 * Creates a supplier or a category from wherever you happen to need one.
 *
 * A delivery arriving from a supplier the system has never heard of is completely ordinary, and
 * sending the clerk to Settings to add it means abandoning the half-keyed delivery. Only a name
 * is asked for — everything else about a supplier is worth filling in later, on the screen built
 * for it, and none of it is needed to book goods in.
 */
export function QuickCreate({
  open,
  what,
  path,
  invalidates,
  initialName,
  onClose,
  onCreated,
}: {
  open: boolean
  /** What is being made, in the words the button used. */
  what: string
  /** Collection endpoint to POST a `{ name }` to. */
  path: string
  /** Query key to refresh, so the picker behind this shows the new row. */
  invalidates: string
  initialName?: string
  onClose: () => void
  onCreated: (created: { id: number; name: string }) => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(initialName ?? '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(initialName ?? '')
      setError(null)
    }
  }, [open, initialName])

  const create = useMutation({
    mutationFn: () =>
      apiClient.post<{ id: number; name: string }>(path, { name: name.trim(), isActive: true }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: [invalidates] })
      onCreated(created)
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : `Could not add that ${what}.`,
      ),
  })

  const trimmed = name.trim()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`New ${what}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={trimmed.length === 0 || create.isPending}
          >
            {create.isPending ? 'Saving…' : 'Add'}
          </Button>
        </>
      }
    >
      <Input
        label={t('Name')}
        value={name}
        autoFocus
        error={error ?? undefined}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && trimmed.length > 0 && !create.isPending) create.mutate()
        }}
      />
    </Modal>
  )
}
